// A PICTURE THAT FAILED TO LOAD ONCE, ASKED FOR AGAIN.
//
// An <img> makes exactly one request in its life. If that request fails — a 502 while the
// server restarts, a connection dropped on a flaky line — the element is broken for as long
// as it stays on the page: the browser never retries, and React re-renders it happily
// because nothing about it has changed. What you get is the broken-picture glyph in a tile
// beside twenty that loaded, and the app looks like it lost the file.
//
// It is not hypothetical here. A deploy SIGTERMs gunicorn, nginx finds no upstream and
// answers whatever is in flight with a 502, and the app is mostly grids of small pictures
// fetched from that server — a part's photographs, a manual's crops, a mesh's renders. One
// restart while a table is loading leaves a scatter of dead tiles, and the only cure was
// reloading the page. Opening one in the zoom "fixed" it, which is the tell: same URL, new
// element, new request.
//
// SO: ONE LISTENER, NOT FORTY-TWO ATTRIBUTES. Resource errors do not bubble, but they can be
// caught in the CAPTURE phase at the window — so a single handler sees every image in the
// app fail, including the ones inside components nobody has touched. The alternative was an
// <img> wrapper swapped in at forty-two sites, which is forty-two chances to miss one and a
// permanent tax on every new one.
//
// It retries twice and then stops. A picture that is genuinely gone should look gone, and a
// retry loop against a 404 is a page that hammers the server about a file that will never
// exist.

// How many times to ask again, and how long to wait first. The waits are for a server that
// is coming back up rather than for a network hiccup: a restart takes a couple of seconds,
// and asking again instantly would spend both retries inside the same outage.
const RETRIES = 2;
const WAIT_MS = [700, 2500];

// Attempts per element, keyed weakly so nothing here keeps a detached <img> alive.
const tries = new WeakMap<HTMLImageElement, number>();

export function watchImageErrors(): () => void {
  const onError = (e: Event) => {
    const img = e.target;
    if (!(img instanceof HTMLImageElement)) return;
    const src = img.currentSrc || img.src;
    // NOT THE ONES THAT CANNOT COME BACK. A blob: URL whose object has been revoked and a
    // malformed data: URL fail permanently and by construction — asking again is asking the
    // same dead pointer. Only a request that went to a server is worth repeating.
    if (!src || src.startsWith('blob:') || src.startsWith('data:')) return;
    const n = tries.get(img) ?? 0;
    if (n >= RETRIES) return;
    tries.set(img, n + 1);

    window.setTimeout(() => {
      // Gone from the page, or filled in by something else in the meantime — a React
      // re-render with a new src, a component unmounted. Either way this is not ours to
      // repair any more.
      if (!img.isConnected || img.naturalWidth > 0) return;
      let url: URL;
      try {
        url = new URL(src, window.location.href);
      } catch {
        return;
      }
      // A DIFFERENT URL, or the browser answers from its own cache — including the cache of
      // the failure. The parameter is the attempt number rather than a clock, so a second
      // failure produces a second distinct URL and not an endless series of them.
      url.searchParams.set('retry', String(n + 1));
      img.src = url.toString();
    }, WAIT_MS[n] ?? WAIT_MS[WAIT_MS.length - 1]);
  };

  window.addEventListener('error', onError, true);
  return () => window.removeEventListener('error', onError, true);
}
